import { type FC, useEffect, useMemo, useRef } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { _isEmpty, _isNotEmpty } from '@/littledash';
import type { ID } from '@/model/Common.model';
import type { SpeciesV1 } from '@/model/Species.model';
import type { StrainV1 } from '@/model/Strain.model';
import { DateUtils } from '@/utils/Date.utils';
import { useEntitySchemaFieldsByType } from '@/utils/entitySchema/useEntitySchema';
import DatePickerNative from '@/components/UI/DatePickerNative';
import { useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import type { AnimalSex } from '@/model/Animal.model';
import { getSexOptions } from '@/model/Animal.utils';
import { featuresSelector } from '@/support/Selectors';

const unknownSexSelector = createSelector([featuresSelector], (features) => ({
  unknownSexEnabled: features?.unknown_sex === true,
}));

interface SpeciesFormProps {
  selectableSpecies: Array<SpeciesV1>;
  defaultStrainId?: ID;
  fieldPath: string;
}

const SpeciesForm: FC<SpeciesFormProps> = ({ selectableSpecies, defaultStrainId, fieldPath }) => {
  const { control, getValues, setValue, trigger } = useFormContext();
  const speciesId = useWatch({ control, name: `${fieldPath}.species_id` });

  const { strainIdToSpeciesId, speciesIdToStrains } = useMemo(() => {
    const strainIdToSpeciesId = new Map<number, number>(
      (selectableSpecies ?? []).flatMap(({ id: speciesId, strains }) =>
        (strains ?? []).map(({ id: strainId }) => [strainId, speciesId])
      )
    );
    const speciesIdToStrains = new Map<number, Array<StrainV1>>(
      (selectableSpecies ?? []).map(({ id: speciesId, strains }) => [speciesId, strains ?? []])
    );
    return { strainIdToSpeciesId, speciesIdToStrains };
  }, [selectableSpecies]);

  const strains = useMemo(() => speciesIdToStrains.get(Number(speciesId)) ?? [], [speciesId, speciesIdToStrains]);

  const prevSpeciesIdRef = useRef(speciesId);
  useEffect(() => {
    if (prevSpeciesIdRef.current !== speciesId) {
      setValue(`${fieldPath}.strain_id`, '');
      prevSpeciesIdRef.current = speciesId;
    }
  }, [speciesId, fieldPath, setValue]);

  if (speciesIdToStrains.size === 0) {
    return null;
  }

  return (
    <div>
      <div className="flex items-top">
        <Controller
          name={`${fieldPath}.species_id`}
          control={control}
          rules={{
            validate: (value) =>
              !(_isNotEmpty(value) && _isEmpty(getValues(`${fieldPath}.strain_id`))) ||
              'Strain is required when Species is selected',
          }}
          render={({ field: { value, onChange, onBlur } }) => (
            <div className="w-50 ph2">
              <label htmlFor={`${fieldPath}.species_id`}>Species</label>
              <select
                id={`${fieldPath}.species_id`}
                style={{ marginBottom: 0 }}
                onBlur={onBlur}
                onChange={onChange}
                defaultValue={strainIdToSpeciesId.get(Number(value ?? defaultStrainId))}
              >
                <option value="">Choose a species</option>
                {selectableSpecies.map(({ id, name }) => (
                  <option key={id} value={id}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
          )}
        />
        <Controller
          name={`${fieldPath}.strain_id`}
          control={control}
          rules={{
            validate: () => {
              trigger(`${fieldPath}.species_id`);
              return true;
            },
          }}
          render={({ field: { value, onChange, onBlur } }) => (
            <div className="w-50 ph2">
              <label htmlFor={`${fieldPath}.strain_id`}>Strain</label>
              <select
                id={`${fieldPath}.strain_id`}
                defaultValue={Number(value)}
                style={{ marginBottom: 0 }}
                disabled={_isEmpty(strains)}
                onChange={onChange}
                onBlur={onBlur}
              >
                <option value="">Choose a strain</option>
                {strains.map(({ id, name, api_id }) => (
                  <option key={id} value={api_id}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
          )}
        />
      </div>
    </div>
  );
};

interface AnimalFormFieldsProps {
  index: number;
  name: string;
  cage_id: number;
  sex: string;
  dob: string | null;
  strain_id?: ID | null;
  selectableSpecies?: Array<SpeciesV1>;
}

const AnimalFormFields: FC<AnimalFormFieldsProps> = ({ index, name, sex, dob, strain_id, selectableSpecies = [] }) => {
  const {
    register,
    control,
    formState: { errors },
  } = useFormContext();
  const entityFieldRequiredMap = useEntitySchemaFieldsByType('animal');
  const isDobRequired = entityFieldRequiredMap?.dob ?? true;
  const { unknownSexEnabled } = useSelector(unknownSexSelector);
  const sexOptions = getSexOptions(unknownSexEnabled);

  const todayInUserTimezone = DateUtils.formatISODateTimeToDate(DateUtils.dateTimeNow());

  const fieldPath = `animals.${index}`;

  return (
    <div className="bg-light-silver ph2 pv3 br2">
      <input type="hidden" {...register(`${fieldPath}.cage_id`)} />
      <div className="mb3 ph2">
        <label htmlFor={`${fieldPath}.name`}>Name</label>
        <input
          type="text"
          id={`${fieldPath}.name`}
          defaultValue={name}
          {...register(`${fieldPath}.name`, { required: 'Name field is required' })}
          style={{ marginBottom: 0 }}
        />
        <ErrorMessage
          errors={errors}
          name={`${fieldPath}.name`}
          render={({ message }) => <p className="f6 red db pt2">{message}</p>}
        />
      </div>
      <div className="mb3 flex items-start">
        <div className="w-50 ph2">
          <label>Sex</label>
          <Controller
            name={`${fieldPath}.sex`}
            defaultValue={sex}
            control={control}
            rules={{
              validate: (value: string) => {
                if (!unknownSexEnabled && value === 'u') {
                  return 'Unknown sex is not available';
                }
                return true;
              },
            }}
            render={({ field: { onChange, value } }) => (
              <select
                id={`${fieldPath}.sex`}
                value={value}
                onChange={(e) => onChange(e.target.value as AnimalSex)}
                data-testid={`animal-${index}-sex-select`}
                style={{ marginBottom: 0 }}
              >
                {sexOptions.map(({ label, value: optValue }) => (
                  <option key={optValue} value={optValue}>
                    {label}
                  </option>
                ))}
              </select>
            )}
          />
        </div>
        <div className="w-50 ph2">
          <label htmlFor={`${fieldPath}.dob`}>D.O.B</label>
          <Controller
            name={`${fieldPath}.dob`}
            control={control}
            rules={{
              validate: (value) => {
                if (_isEmpty(value)) {
                  return !isDobRequired || 'DOB is required';
                }
                return value <= todayInUserTimezone || 'DOB cannot be in the future';
              },
            }}
            defaultValue={isDobRequired ? dob : ''}
            render={({ field: { onChange, value } }) => (
              <DatePickerNative
                value={value}
                max={todayInUserTimezone}
                style={{ marginBottom: 0 }}
                onChange={onChange}
              />
            )}
          />
          <ErrorMessage
            errors={errors}
            name={`${fieldPath}.dob`}
            render={({ message }) => <p className="f6 red db pt2">{message}</p>}
          />
        </div>
      </div>
      <SpeciesForm
        selectableSpecies={selectableSpecies}
        defaultStrainId={strain_id ?? undefined}
        fieldPath={fieldPath}
      />
      <ErrorMessage
        errors={errors}
        name={`${fieldPath}.species_id`}
        render={({ message }) => <p className="f6 red db pt2 ph2">{message}</p>}
      />
    </div>
  );
};

export default AnimalFormFields;
